<script setup lang="ts">
    import { Button } from '@/components/ui/button';
    import {
        Dialog,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogClose,
        DialogFooter,
        DialogDescription,
        DialogBody,
    } from '@/components/ui/dialog';
    import { computed, ref } from 'vue';
    import { useI18n } from 'vue-i18n';
    import type { Payslip, AuthUser } from '@/types';
    import { toast } from 'vue-sonner';
    import { useForm } from '@inertiajs/vue3';
    import { InputRow } from '@/components/ui/form';
    import { Textarea } from '@/components/ui/textarea';
    import { useDateFormat } from '@/composables/useDateFormat';
    import QueryKey from '@/enums/QueryKey';
    import { useQueryClient } from '@tanstack/vue-query';
    import { useMutation } from '@tanstack/vue-query';
    import axios from 'axios';
    import { Alert } from '../ui/alert';

    const { formatDMYFromIso } = useDateFormat();

    const queryClient = useQueryClient();

    const props = defineProps<{
        payslip: Payslip;
        modelValue?: boolean;
        user: AuthUser;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: boolean];
    }>();

    const open = computed({
        get: () => props.modelValue ?? false,
        set: (value) => emit('update:modelValue', value),
    });

    const { t } = useI18n();

    const form = useForm({
        id: props.payslip.id,
        comment: '',
    });

    const rejectPayslip = () => {
        form.post(route('v2.salary.payslip.reject'), {
            preserveScroll: true,
            onSuccess: () => {
                form.resetAndClearErrors();
                toast.success(t('salary.pay_slips.reject.success'));
                queryClient.invalidateQueries({
                    queryKey: [QueryKey.PAYROLLS, props.user.selected_company_id],
                });
                handleClose();
            },
            onError: (error: any) => {
                toast.error(t('salary.pay_slips.reject.error'));
            },
        });
    };

    const handleClose = () => {
        open.value = false;
    };
</script>

<template>
    <Dialog
        :open="open"
        @update:open="(value) => (open = value)"
    >
        <DialogContent class="sm:max-w-lg">
            <form
                class="flex max-h-full flex-col overflow-hidden"
                @submit.prevent="rejectPayslip"
            >
                <DialogHeader>
                    <DialogTitle>
                        {{ t('salary.pay_slips.reject.header') }}
                    </DialogTitle>
                </DialogHeader>

                <DialogBody class="space-y-5">
                    <Alert variant="warning">
                        {{ t('salary.pay_slips.reject.alert') }}
                    </Alert>

                    <InputRow
                        :form="form"
                        name="comment"
                        label="salary.sheets.comment"
                    >
                        <Textarea
                            id="comment"
                            v-model="form.comment"
                            :placeholder="t('pay-slips.comment')"
                        />
                    </InputRow>
                </DialogBody>

                <DialogFooter class="gap-2 border-t sm:justify-center">
                    <DialogClose as-child>
                        <Button
                            variant="outline"
                            size="sm"
                            @click="handleClose"
                        >
                            {{ t('forms.close') }}
                        </Button>
                    </DialogClose>

                    <Button
                        variant="destructive"
                        :disabled="form.processing"
                        :loading="form.processing"
                        size="sm"
                    >
                        {{ t('pay-slips.reject') }}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>
